import {
	BN,
	PublicKey,
	InsuranceFundStake,
	getTokenAmount,
	SpotBalanceType,
	ZERO,
	unstakeSharesToAmount,
	BigNum,
	unstakeSharesToAmountWithOpenRequest,
	timeRemainingUntilUpdate,
	QUOTE_PRECISION_EXP,
	SpotMarketAccount,
	PERCENTAGE_PRECISION,
} from '@velocity-exchange/sdk';
import { UIMarket, getPausedOperations } from '@velocity-exchange/common';

export interface IfData {
	userStake: {
		loaded: boolean;
		account?: InsuranceFundStake;
		costBasisBigNum?: BigNum;
		costBasis?: number;
		currentStakeBigNum?: BigNum;
		currentStake?: number;
		currentShares?: BN;
		unstakeRequest?: UnstakeRequest;
		currentStakeNotionalValue?: BigNum;
	};
	vault: {
		loaded: boolean;
		account?: {
			vault: PublicKey;
			totalShares: BN;
			userShares: BN;
			sharesBase: BN;
			unstakingPeriod: BN;
			lastRevenueSettleTs: BN;
			revenueSettlePeriod: BN;
			ifFeeFactor: number;
		};
		revenuePoolBigNum?: BigNum;
		totalStakeBigNum?: BigNum;
		totalStake?: number;
		totalStakeNotionalValue?: BigNum;
		nextPayoutTotal?: number;
		/**
		 * How much will be paid out to stakers on the next settle if the revenue pool stays the same
		 */
		nextPayoutForStakers?: number;
		/**
		 * The actual ratio of the current revenue pool that will be paid out to stakers (e.g.: 0.05 means 5% of the pool is paid to stakers each settle period)
		 */
		actualRatioForStakers?: number;
		/*
		 * Ratio of each settlement payout that goes to stakers (rest goes to protocol)
		 */
		ratioForStakers?: number;
		/**
		 * Ratio of the revenue pool balance paid out each settle period
		 */
		payoutRatio?: number;
		/*
		 * Revenue pool balance as a number
		 */
		revenuePool?: number;
		/**
		 * Next APR as a percentage (e.g.: 35 means 35% APR)
		 */
		nextApr?: number;
		/**
		 * 7-day APY calculated from share price percent diff
		 */
		sevenDayApy?: number;
		/*
		 * Max APR of staker and protocol share combined. multiply by ratioForStakers to get stakers' max APR)
		 */
		maxApr?: number;
		/*
		 * Seconds until next revenue settle
		 */
		secondsUntilNextRevenueSettle?: number;
		/**
		 * Timestamp of last revenue settle in ms, can be used to create a js Date
		 */
		lastRevenueSettleMs?: number;
		revenueSettlePeriodMinutes?: number;
	};
	pausedOperations: string[];
	marketIndex: number;
}

const MAX_APR = 1000;

export interface UnstakeRequest {
	amountBigNum: BigNum;
	amount: number;
	timestamp: number;
}

/**
 * Fetches insurance fund data one time for the given spot market
 *
 * @param spotMarket	Spot market account
 * @param oraclePrice	Current oracle price of the given spot market
 * @param connected		Is the user connected to a wallet?
 */
export const fetchInsuranceFundData = (
	spotMarket: SpotMarketAccount,
	oraclePrice: number,
	vaultBalanceBigNum: BigNum,
	userIfStakeAccount?: InsuranceFundStake,
	connected?: boolean,
	sevenDayApy?: number
) => {
	const { precisionExp } = UIMarket.spotMarkets[spotMarket.marketIndex];

	const newIfData: IfData = {
		userStake: {
			loaded: false,
		},
		vault: {
			loaded: false,
		},
		pausedOperations: [],
		marketIndex: undefined as unknown as number,
	};

	let ifStakeAccount: InsuranceFundStake | null = null;
	let hasExistingUnstakeRequest = false;

	if (connected) {
		// Get the user's IF stake account for the market (if it exists)
		try {
			ifStakeAccount = userIfStakeAccount ?? null;

			if (
				ifStakeAccount?.lastWithdrawRequestValue &&
				ifStakeAccount.lastWithdrawRequestValue.gt(ZERO)
			) {
				hasExistingUnstakeRequest = true;

				const unstakeRequestAmountBigNum = BigNum.from(
					ifStakeAccount.lastWithdrawRequestValue,
					precisionExp
				);

				newIfData.userStake.unstakeRequest = {
					amountBigNum: unstakeRequestAmountBigNum,
					amount: unstakeRequestAmountBigNum.toNum(),
					timestamp: ifStakeAccount?.lastWithdrawRequestTs.toNumber() * 1000,
				};
			}

			newIfData.userStake.account = ifStakeAccount ?? undefined;
			newIfData.userStake.loaded = true;
		} catch (err) {
			// console.log(err);
			newIfData.userStake.account = undefined;
			newIfData.userStake.loaded = true;
		}
	} else {
		newIfData.userStake.account = undefined;
		newIfData.userStake.loaded = true;
	}

	// Get global stats for the market's IF vault
	try {
		const vaultBalance = vaultBalanceBigNum.toNum();

		// Conmputing the APR:
		const revenuePoolBN = getTokenAmount(
			spotMarket.revenuePool.scaledBalance,
			spotMarket,
			SpotBalanceType.DEPOSIT
		);
		const revenuePoolBigNum = BigNum.from(revenuePoolBN, precisionExp);
		const revenuePool = revenuePoolBigNum.toNum();

		const payoutRatio = 0.1;
		// `totalFactor`/`userFactor` were removed from the InsuranceFund account in
		// @velocity-exchange/sdk. The staker share is now approximated from the
		// single remaining `ifFeeFactor` (out of PERCENTAGE_PRECISION). See
		// velocity-migration-followups.md.
		const ratioForStakers =
			spotMarket.insuranceFund.ifFeeFactor > 0
				? spotMarket.insuranceFund.ifFeeFactor / PERCENTAGE_PRECISION.toNumber()
				: 0;
		const actualRatioForStakers = ratioForStakers * payoutRatio;

		// Settle periods from on-chain data:
		const revSettlePeriod =
			spotMarket.insuranceFund.revenueSettlePeriod.toNumber() * 1000;

		const settlesPerYear = 31536000000 / revSettlePeriod;

		const projectedAnnualRev = revenuePool * settlesPerYear * payoutRatio;

		const uncappedApr =
			vaultBalance === 0 ? 0 : (projectedAnnualRev / vaultBalance) * 100;
		const cappedApr = Math.min(uncappedApr, MAX_APR);

		const nextApr = cappedApr * ratioForStakers;

		// When uncappedApr is 0 (e.g. an empty vault, vaultBalance === 0),
		// cappedApr is also 0 and cappedApr / uncappedApr = 0 / 0 = NaN, which
		// propagates into the displayed payout. The limit of the cap ratio as
		// the APR falls below the cap is 1 (no capping applies), so use that.
		const cappingRatio = uncappedApr === 0 ? 1 : cappedApr / uncappedApr;
		const nextPayoutTotal = revenuePool * payoutRatio * cappingRatio;
		const nextPayoutForStakers = nextPayoutTotal * ratioForStakers;

		const lastRevSettle =
			spotMarket.insuranceFund.lastRevenueSettleTs.toNumber() * 1000;

		const currentStakeBN = hasExistingUnstakeRequest
			? unstakeSharesToAmountWithOpenRequest(
					ifStakeAccount?.ifShares || new BN(0),
					ifStakeAccount?.lastWithdrawRequestShares || new BN(0),
					ifStakeAccount?.lastWithdrawRequestValue || new BN(0),
					spotMarket.insuranceFund.totalShares,
					vaultBalanceBigNum.val
				)
			: unstakeSharesToAmount(
					ifStakeAccount?.ifShares || new BN(0),
					spotMarket.insuranceFund.totalShares,
					vaultBalanceBigNum.val
				);

		const currentStakeBigNum = BigNum.from(currentStakeBN, precisionExp);
		const currentStake = currentStakeBigNum.toNum();

		const costBasisBigNum = BigNum.from(
			ifStakeAccount?.costBasis || ZERO,
			precisionExp
		);
		const costBasis = costBasisBigNum.toNum();

		const timeUntilNextSettleBn = timeRemainingUntilUpdate(
			new BN(Math.round(Date.now() / 1000)),
			spotMarket.insuranceFund.lastRevenueSettleTs,
			spotMarket.insuranceFund.revenueSettlePeriod
		);

		newIfData.userStake.costBasisBigNum = costBasisBigNum;
		newIfData.userStake.costBasis = costBasis;
		newIfData.userStake.currentStakeBigNum = currentStakeBigNum;
		newIfData.userStake.currentStake = currentStake;
		newIfData.userStake.currentShares = ifStakeAccount?.ifShares;
		newIfData.userStake.currentStakeNotionalValue = BigNum.fromPrint(
			`${currentStakeBigNum.toNum() * oraclePrice}`,
			QUOTE_PRECISION_EXP
		);

		newIfData.vault.nextApr = revSettlePeriod === 0 ? 0 : nextApr;
		newIfData.vault.sevenDayApy = sevenDayApy;

		newIfData.vault.nextPayoutTotal = nextPayoutTotal;
		newIfData.vault.nextPayoutForStakers = nextPayoutForStakers;
		newIfData.vault.actualRatioForStakers = actualRatioForStakers;
		newIfData.vault.ratioForStakers = ratioForStakers;
		newIfData.vault.revenuePool = revenuePool;
		newIfData.vault.payoutRatio = payoutRatio;
		newIfData.vault.revenuePoolBigNum = revenuePoolBigNum;
		newIfData.vault.totalStakeBigNum = vaultBalanceBigNum;
		newIfData.vault.totalStake = vaultBalance;
		newIfData.vault.account = spotMarket.insuranceFund;
		newIfData.vault.loaded = true;
		newIfData.vault.secondsUntilNextRevenueSettle =
			timeUntilNextSettleBn.toNumber();
		newIfData.vault.lastRevenueSettleMs = lastRevSettle;
		newIfData.vault.maxApr = MAX_APR;
		newIfData.vault.revenueSettlePeriodMinutes = revSettlePeriod / 1000 / 60;

		const quoteBalance = BigNum.fromPrint(
			`${vaultBalanceBigNum.toNum() * oraclePrice}`,
			QUOTE_PRECISION_EXP
		);

		newIfData.vault.totalStakeNotionalValue = quoteBalance;
		newIfData.pausedOperations = getPausedOperations(spotMarket);
		newIfData.marketIndex = spotMarket.marketIndex;
	} catch (err) {
		// console.log(err);
		newIfData.vault.loaded = true;
		newIfData.vault.account = undefined;
	}

	return newIfData;
};
